import React, { useState, useEffect, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import { uploadData, list, getUrl, remove } from 'aws-amplify/storage';
import { signIn, signOut, getCurrentUser, confirmSignIn, fetchAuthSession, setUpTOTP, verifyTOTPSetup, fetchMFAPreference, updateMFAPreference } from 'aws-amplify/auth';
import { useDropzone } from 'react-dropzone';
import CredentialGenerator from './CredentialGenerator';
import ManageCredentials from './ManageCredentials';
import './App.css';

// Configure Amplify - these values should be set via environment variables
const amplifyConfig = {
    Auth: {
        Cognito: {
            userPoolId: process.env.REACT_APP_USER_POOL_ID,
            userPoolClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID,
            identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID,
            region: process.env.REACT_APP_AWS_REGION || 'us-west-2',
            allowGuestAccess: false
        }
    },
    Storage: {
        S3: {
            bucket: process.env.REACT_APP_S3_BUCKET,
            region: process.env.REACT_APP_AWS_REGION || 'us-west-2',
            bucketAccelerateEndpoint: true // Enable S3 Transfer Acceleration
        }
    }
};

console.log('Amplify Config:', {
    userPoolId: amplifyConfig.Auth.Cognito.userPoolId,
    identityPoolId: amplifyConfig.Auth.Cognito.identityPoolId,
    bucket: amplifyConfig.Storage.S3.bucket,
    region: amplifyConfig.Storage.S3.region
});

Amplify.configure(amplifyConfig);

function App() {
    const [uploads, setUploads] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [user, setUser] = useState(null);
    const [userGroups, setUserGroups] = useState([]);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
    const [needsMfaSetup, setNeedsMfaSetup] = useState(false);
    const [needsMfaCode, setNeedsMfaCode] = useState(false);
    const [mfaCode, setMfaCode] = useState('');
    const [totpUri, setTotpUri] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [showAdmin, setShowAdmin] = useState(false);
    const [showMfaSettings, setShowMfaSettings] = useState(false);
    const [mfaEnabled, setMfaEnabled] = useState(false);
    const [mfaSetupInProgress, setMfaSetupInProgress] = useState(false);
    const [mfaSetupUri, setMfaSetupUri] = useState('');
    const [mfaSetupCode, setMfaSetupCode] = useState('');
    const [userEmailMap, setUserEmailMap] = useState({});
    const [loadingEmails, setLoadingEmails] = useState(false);

    const isAdmin = () => {
        const admin = userGroups.includes('Admins');
        console.log('Is admin?', admin, 'Groups:', userGroups);
        return admin;
    };

    const loadUploadedFiles = useCallback(async () => {
        try {
            const session = await fetchAuthSession();
            const identityId = session.identityId;
            // Extract just the UUID part (remove region prefix)
            const identityUuid = identityId.split(':')[1] || identityId;
            
            // Admins see all files, users see only their own
            if (userGroups.includes('Admins')) {
                const result = await list({
                    prefix: 'forensic-images/',
                    options: {
                        accessLevel: 'guest' // Use guest level to avoid public/ prefix
                    }
                });
                setUploadedFiles(result.items || []);
                
                // Fetch user emails from mapping file (admins only)
                await loadUserEmailsFromCognito();
            } else {
                // Users only see their own files (using UUID only)
                const result = await list({
                    prefix: `forensic-images/${identityUuid}/`,
                    options: {
                        accessLevel: 'guest' // Use guest level to avoid public/ prefix
                    }
                });
                setUploadedFiles(result.items || []);
                // Regular users don't need the email mapping
            }
        } catch (err) {
            console.error('Error loading files:', err);
        }
    }, [userGroups]);

    const loadUserEmailsFromCognito = async () => {
        setLoadingEmails(true);
        try {
            console.log('Loading user mapping from S3...');
            
            // Try to load the user mapping file from S3
            try {
                const result = await getUrl({
                    key: 'user-mappings.json',
                    options: {
                        accessLevel: 'guest'
                    }
                });
                
                const response = await fetch(result.url.toString());
                if (response.ok) {
                    const mappings = await response.json();
                    console.log('Loaded user mappings:', mappings);
                    setUserEmailMap(mappings);
                } else if (response.status === 404) {
                    console.log('User mappings file does not exist yet (will be created on first upload)');
                } else {
                    console.log('Could not load user mappings, status:', response.status);
                }
            } catch (err) {
                // Silently handle errors - file might not exist yet
                console.log('User mappings file not found (will be created on first upload)');
            }
        } catch (err) {
            console.error('Error loading user emails:', err);
        } finally {
            setLoadingEmails(false);
        }
    };

    const updateUserMapping = async (identityUuid, email) => {
        try {
            // Load existing mappings
            let mappings = {};
            try {
                const result = await getUrl({
                    key: 'user-mappings.json',
                    options: {
                        accessLevel: 'guest'
                    }
                });
                const response = await fetch(result.url.toString());
                if (response.ok) {
                    mappings = await response.json();
                }
            } catch (err) {
                console.log('No existing mappings, creating new');
            }

            // Update mapping
            mappings[identityUuid] = email;

            // Save back to S3
            const mappingBlob = new Blob([JSON.stringify(mappings, null, 2)], { type: 'application/json' });
            await uploadData({
                key: 'user-mappings.json',
                data: mappingBlob,
                options: {
                    accessLevel: 'guest',
                    contentType: 'application/json'
                }
            }).result;

            console.log('Updated user mapping:', identityUuid, '->', email);
        } catch (err) {
            console.error('Error updating user mapping:', err);
        }
    };

    useEffect(() => {
        checkUser();
    }, []);

    useEffect(() => {
        if (user) {
            loadUploadedFiles();
            checkMfaStatus();
        }
    }, [user, loadUploadedFiles]);

    const checkUser = async () => {
        try {
            const currentUser = await getCurrentUser();
            const session = await fetchAuthSession();
            const groups = session.tokens?.accessToken?.payload['cognito:groups'] || [];
            console.log('User groups:', groups);
            console.log('Session:', session.tokens?.accessToken?.payload);
            setUser(currentUser);
            setUserGroups(groups);
        } catch (err) {
            console.error('Check user error:', err);
            setUser(null);
            setUserGroups([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSignIn = async (e) => {
        e.preventDefault();
        setError('');
        try {
            const result = await signIn({ username: email, password });
            
            const step = result.nextStep?.signInStep;
            if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                setNeedsPasswordChange(true);
            } else if (step === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
                // First-time MFA setup — generate TOTP secret
                const totpSetup = result.nextStep.totpSetupDetails;
                const uri = totpSetup.getSetupUri('PCC Forensic Upload', email);
                setTotpUri(uri.toString());
                setNeedsMfaSetup(true);
            } else if (step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
                setNeedsMfaCode(true);
            } else {
                await checkUser();
            }
        } catch (err) {
            console.error('Sign in error:', err);
            setError(err.message || 'Failed to sign in');
        }
    };

    const handleMfaSetupVerify = async (e) => {
        e.preventDefault();
        setError('');
        try {
            await confirmSignIn({ challengeResponse: mfaCode });
            setNeedsMfaSetup(false);
            setMfaCode('');
            setTotpUri('');
            await checkUser();
        } catch (err) {
            console.error('MFA setup error:', err);
            setError(err.message || 'Invalid verification code. Please try again.');
        }
    };

    const handleMfaVerify = async (e) => {
        e.preventDefault();
        setError('');
        try {
            await confirmSignIn({ challengeResponse: mfaCode });
            setNeedsMfaCode(false);
            setMfaCode('');
            await checkUser();
        } catch (err) {
            console.error('MFA verify error:', err);
            setError(err.message || 'Invalid code. Please try again.');
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setError('');
        try {
            await confirmSignIn({ challengeResponse: newPassword });
            setNeedsPasswordChange(false);
            await checkUser();
        } catch (err) {
            console.error('Password change error:', err);
            setError(err.message || 'Failed to change password');
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            setUser(null);
            setUploads([]);
        } catch (err) {
            console.error('Sign out error:', err);
        }
    };

    const checkMfaStatus = async () => {
        try {
            const preference = await fetchMFAPreference();
            setMfaEnabled(preference?.preferred === 'TOTP' || preference?.enabled?.includes('TOTP'));
        } catch (err) {
            console.error('Error checking MFA status:', err);
            setMfaEnabled(false);
        }
    };

    const handleStartMfaSetup = async () => {
        setError('');
        setMfaSetupCode('');
        try {
            const totpSetup = await setUpTOTP();
            const userEmail = user.signInDetails?.loginId || user.username;
            const uri = totpSetup.getSetupUri('PCC Forensic Upload', userEmail);
            setMfaSetupUri(uri.toString());
            setMfaSetupInProgress(true);
        } catch (err) {
            console.error('MFA setup error:', err);
            setError(err.message || 'Failed to start MFA setup');
        }
    };

    const handleCompleteMfaSetup = async (e) => {
        e.preventDefault();
        setError('');
        try {
            await verifyTOTPSetup({ code: mfaSetupCode });
            await updateMFAPreference({ totp: 'PREFERRED' });
            setMfaEnabled(true);
            setMfaSetupInProgress(false);
            setMfaSetupUri('');
            setMfaSetupCode('');
            setSuccessMessage('MFA has been enabled for your account.');
        } catch (err) {
            console.error('MFA verify error:', err);
            setError(err.message || 'Invalid verification code. Please try again.');
        }
    };

    const handleDisableMfa = async () => {
        setError('');
        try {
            await updateMFAPreference({ totp: 'DISABLED' });
            setMfaEnabled(false);
            setSuccessMessage('MFA has been disabled for your account.');
        } catch (err) {
            console.error('MFA disable error:', err);
            setError(err.message || 'Failed to disable MFA');
        }
    };

    const onDrop = async (acceptedFiles) => {
        if (acceptedFiles.length === 0) return;

        setUploading(true);
        const newUploads = acceptedFiles.map(file => ({
            file,
            progress: 0,
            status: 'uploading',
            error: null
        }));

        setUploads(prev => [...prev, ...newUploads]);

        // Get current user info for file organization
        const currentUser = await getCurrentUser();
        const session = await fetchAuthSession();
        const userEmail = currentUser.signInDetails?.loginId || currentUser.username;
        const identityId = session.identityId;
        
        // Extract just the UUID part from the identity ID (remove region prefix)
        const identityUuid = identityId.split(':')[1] || identityId;

        for (let i = 0; i < acceptedFiles.length; i++) {
            const file = acceptedFiles[i];
            const uploadIndex = uploads.length + i;

            try {
                const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                // Use only UUID in path for cleaner structure
                const key = `forensic-images/${identityUuid}/${timestamp}_${file.name}`;

                await uploadData({
                    key,
                    data: file,
                    options: {
                        accessLevel: 'guest', // Use guest level to avoid public/ prefix
                        contentType: file.type,
                        metadata: {
                            uploadedBy: userEmail,
                            uploadedAt: new Date().toISOString(),
                            identityId: identityUuid
                        },
                        onProgress: ({ transferredBytes, totalBytes }) => {
                            const progress = Math.round((transferredBytes / totalBytes) * 100);
                            setUploads(prev => {
                                const updated = [...prev];
                                updated[uploadIndex] = { ...updated[uploadIndex], progress };
                                return updated;
                            });
                        }
                    }
                }).result;

                // Update user mapping file
                await updateUserMapping(identityUuid, userEmail);

                setUploads(prev => {
                    const updated = [...prev];
                    updated[uploadIndex] = { ...updated[uploadIndex], status: 'completed', progress: 100 };
                    return updated;
                });
                
                // Reload file list after successful upload (catch errors silently)
                try {
                    await loadUploadedFiles();
                } catch (listError) {
                    console.log('Could not refresh file list:', listError);
                    // Don't show error to user - upload was successful
                }
            } catch (error) {
                console.error('Upload error:', error);
                setUploads(prev => {
                    const updated = [...prev];
                    updated[uploadIndex] = {
                        ...updated[uploadIndex],
                        status: 'error',
                        error: error.message
                    };
                    return updated;
                });
            }
        }

        setUploading(false);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        disabled: uploading
    });

    const clearCompleted = () => {
        setUploads(prev => prev.filter(u => u.status !== 'completed'));
    };

    const handleDownload = async (fileKey) => {
        try {
            setError('');
            setSuccessMessage('');
            
            const result = await getUrl({
                key: fileKey,
                options: {
                    accessLevel: 'guest', // Use guest level to avoid public/ prefix
                    expiresIn: 300 // URL expires in 5 minutes
                }
            });
            
            // Open the URL in a new tab to trigger download
            window.open(result.url.toString(), '_blank');
        } catch (error) {
            console.error('Download error:', error);
            setError(`Failed to download file: ${error.message}`);
        }
    };

    const handleDelete = async (fileKey, fileName) => {
        if (!window.confirm(`Are you sure you want to delete ${fileName}?`)) {
            return;
        }

        try {
            setError('');
            setSuccessMessage('');
            
            await remove({
                key: fileKey,
                options: {
                    accessLevel: 'guest' // Use guest level to avoid public/ prefix
                }
            });
            
            // Show success message
            setSuccessMessage(`File "${fileName}" deleted successfully`);
            
            // Reload file list after deletion
            await loadUploadedFiles();
            
            // Clear success message after 5 seconds
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (error) {
            console.error('Delete error:', error);
            setError(`Failed to delete file "${fileName}": ${error.message}`);
        }
    };

    if (loading) {
        return (
            <div className="App">
                <div className="App-header">
                    <h1>🔒 PCC Security Forensic Upload</h1>
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        if (needsMfaSetup) {
            return (
                <div className="App">
                    <header className="App-header">
                        <h1>🔒 PCC Security Forensic Upload</h1>
                        <p>Set Up Two-Factor Authentication</p>
                    </header>

                    <main className="App-main">
                        <div className="login-container">
                            <h2>Set Up Authenticator App</h2>
                            <form onSubmit={handleMfaSetupVerify} className="login-form">
                                {error && <div className="error-message">{error}</div>}
                                <div className="mfa-setup-instructions">
                                    <p>1. Install an authenticator app (Google Authenticator, Microsoft Authenticator, or Authy)</p>
                                    <p>2. Scan this QR code or enter the secret manually:</p>
                                    <div className="qr-code-container">
                                        <img 
                                            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(totpUri)}`} 
                                            alt="MFA QR Code"
                                            className="qr-code"
                                        />
                                    </div>
                                    <details className="manual-entry">
                                        <summary>Can't scan? Enter manually</summary>
                                        <code className="totp-secret">{totpUri.split('secret=')[1]?.split('&')[0]}</code>
                                    </details>
                                    <p>3. Enter the 6-digit code from your app:</p>
                                </div>
                                <div className="form-group">
                                    <label>Verification Code</label>
                                    <input
                                        type="text"
                                        value={mfaCode}
                                        onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        required
                                        maxLength="6"
                                        pattern="[0-9]{6}"
                                        placeholder="000000"
                                        autoComplete="one-time-code"
                                        className="mfa-input"
                                    />
                                </div>
                                <button type="submit" className="login-btn">Verify & Complete Setup</button>
                            </form>
                        </div>
                    </main>
                </div>
            );
        }

        if (needsMfaCode) {
            return (
                <div className="App">
                    <header className="App-header">
                        <h1>🔒 PCC Security Forensic Upload</h1>
                        <p>Two-Factor Authentication</p>
                    </header>

                    <main className="App-main">
                        <div className="login-container">
                            <h2>Enter Verification Code</h2>
                            <form onSubmit={handleMfaVerify} className="login-form">
                                {error && <div className="error-message">{error}</div>}
                                <p className="mfa-prompt">Enter the 6-digit code from your authenticator app</p>
                                <div className="form-group">
                                    <label>Verification Code</label>
                                    <input
                                        type="text"
                                        value={mfaCode}
                                        onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        required
                                        maxLength="6"
                                        pattern="[0-9]{6}"
                                        placeholder="000000"
                                        autoComplete="one-time-code"
                                        className="mfa-input"
                                    />
                                </div>
                                <button type="submit" className="login-btn">Verify</button>
                            </form>
                        </div>
                    </main>
                </div>
            );
        }

        if (needsPasswordChange) {
            return (
                <div className="App">
                    <header className="App-header">
                        <h1>🔒 PCC Security Forensic Upload</h1>
                        <p>Change Your Password</p>
                    </header>

                    <main className="App-main">
                        <div className="login-container">
                            <h2>Set New Password</h2>
                            <form onSubmit={handlePasswordChange} className="login-form">
                                {error && <div className="error-message">{error}</div>}
                                <div className="form-group">
                                    <label>New Password</label>
                                    <input
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                        minLength="8"
                                        autoComplete="new-password"
                                    />
                                    <small>Minimum 8 characters, must include uppercase, lowercase, and number</small>
                                </div>
                                <button type="submit" className="login-btn">Change Password</button>
                            </form>
                        </div>
                    </main>
                </div>
            );
        }

        return (
            <div className="App">
                <header className="App-header">
                    <h1>🔒 PCC Security Forensic Upload</h1>
                    <p>Secure file upload portal with S3 Transfer Acceleration</p>
                </header>

                <main className="App-main">
                    <div className="login-container">
                        <h2>Sign In</h2>
                        <form onSubmit={handleSignIn} className="login-form">
                            {error && <div className="error-message">{error}</div>}
                            <div className="form-group">
                                <label>Email</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="username"
                                />
                            </div>
                            <div className="form-group">
                                <label>Password</label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                />
                            </div>
                            <button type="submit" className="login-btn">Sign In</button>
                        </form>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="App">
            <header className="App-header">
                <div>
                    <h1>🔒 PCC Security Forensic Upload</h1>
                    <p>Secure file upload portal with S3 Transfer Acceleration</p>
                    {isAdmin() && <span className="admin-badge">Admin</span>}
                </div>
                <div className="header-actions">
                    <span className="logged-in-user">{user.signInDetails?.loginId || user.username}</span>
                    <button onClick={() => { setShowMfaSettings(!showMfaSettings); setShowAdmin(false); }} className="security-btn">
                        🔐 Security
                    </button>
                    {isAdmin() && (
                        <button onClick={() => { setShowAdmin(!showAdmin); setShowMfaSettings(false); }} className="admin-btn">
                            {showAdmin ? 'Upload' : 'Admin Panel'}
                        </button>
                    )}
                    <button onClick={handleSignOut} className="signout-btn">Sign Out</button>
                </div>
            </header>

            <main className="App-main">
                {showMfaSettings ? (
                    <div className="mfa-settings-panel">
                        <h2>Security Settings</h2>
                        <div className="mfa-status">
                            <span className="mfa-label">Two-Factor Authentication (TOTP)</span>
                            <span className={`mfa-badge ${mfaEnabled ? 'enabled' : 'disabled'}`}>
                                {mfaEnabled ? '✓ Enabled' : '✗ Disabled'}
                            </span>
                        </div>
                        
                        {!mfaEnabled && !mfaSetupInProgress && (
                            <div className="mfa-action">
                                <p>Add an extra layer of security to your account by enabling two-factor authentication.</p>
                                <button onClick={handleStartMfaSetup} className="enable-mfa-btn">
                                    Enable MFA
                                </button>
                            </div>
                        )}

                        {mfaSetupInProgress && (
                            <div className="mfa-setup-flow">
                                <div className="mfa-setup-instructions">
                                    <p>1. Install an authenticator app (Google Authenticator, Microsoft Authenticator, or Authy)</p>
                                    <p>2. Scan this QR code:</p>
                                    <div className="qr-code-container">
                                        <img 
                                            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(mfaSetupUri)}`} 
                                            alt="MFA QR Code"
                                            className="qr-code"
                                        />
                                    </div>
                                    <details className="manual-entry">
                                        <summary>Can't scan? Enter manually</summary>
                                        <code className="totp-secret">{mfaSetupUri.split('secret=')[1]?.split('&')[0]}</code>
                                    </details>
                                    <p>3. Enter the 6-digit code from your app:</p>
                                </div>
                                <form onSubmit={handleCompleteMfaSetup} className="mfa-verify-form">
                                    {error && <div className="error-message">{error}</div>}
                                    <input
                                        type="text"
                                        value={mfaSetupCode}
                                        onChange={(e) => setMfaSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        required
                                        maxLength="6"
                                        pattern="[0-9]{6}"
                                        placeholder="000000"
                                        autoComplete="one-time-code"
                                        className="mfa-input"
                                    />
                                    <div className="mfa-setup-actions">
                                        <button type="submit" className="verify-btn">Verify & Enable</button>
                                        <button type="button" onClick={() => { setMfaSetupInProgress(false); setMfaSetupUri(''); setMfaSetupCode(''); }} className="cancel-btn">
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {mfaEnabled && (
                            <div className="mfa-action">
                                <p>Your account is protected with two-factor authentication.</p>
                                <button onClick={handleDisableMfa} className="disable-mfa-btn">
                                    Disable MFA
                                </button>
                            </div>
                        )}
                    </div>
                ) : showAdmin && isAdmin() ? (
                    <AdminPanel userPoolId={amplifyConfig.Auth.Cognito.userPoolId} />
                ) : (
                    <>
                        {uploads.length > 0 && (
                            <div className="uploads-section">
                                <div className="uploads-header">
                                    <h2>Upload Queue</h2>
                                    {uploads.some(u => u.status === 'completed') && (
                                        <button onClick={clearCompleted} className="clear-btn">
                                            Clear Completed
                                        </button>
                                    )}
                                </div>

                                <div className="uploads-list">
                                    {uploads.map((upload, index) => (
                                        <div key={index} className={`upload-item ${upload.status}`}>
                                            <div className="upload-info">
                                                <span className="file-name">{upload.file.name}</span>
                                                <span className="file-size">
                                                    {(upload.file.size / (1024 * 1024)).toFixed(2)} MB
                                                </span>
                                            </div>

                                            {upload.status === 'uploading' && (
                                                <div className="progress-bar">
                                                    <div
                                                        className="progress-fill"
                                                        style={{ width: `${upload.progress}%` }}
                                                    />
                                                    <span className="progress-text">{upload.progress}%</span>
                                                </div>
                                            )}

                                            {upload.status === 'completed' && (
                                                <div className="status-badge success">✓ Uploaded</div>
                                            )}

                                            {upload.status === 'error' && (
                                                <div className="status-badge error">
                                                    ✗ Error: {upload.error}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div
                            {...getRootProps()}
                            className={`dropzone ${isDragActive ? 'active' : ''} ${uploading ? 'disabled' : ''}`}
                        >
                            <input {...getInputProps()} />
                            <div className="dropzone-content">
                                <svg className="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                {isDragActive ? (
                                    <p>Drop files here...</p>
                                ) : (
                                    <>
                                        <p>Drag & drop forensic images here</p>
                                        <p className="dropzone-hint">or click to select files</p>
                                    </>
                                )}
                            </div>
                        </div>

                        <CredentialGenerator />

                        {uploadedFiles.length > 0 && (
                            <div className="files-section">
                                <h2>Uploaded Files</h2>
                                {isAdmin() ? (
                                    // Admin view: Group files by owner
                                    (() => {
                                        // Group files by user ID
                                        const filesByUser = {};
                                        uploadedFiles.forEach(file => {
                                            // Extract user ID from path
                                            const pathParts = file.key.replace('forensic-images/', '').replace('public/forensic-images/', '').split('/');
                                            const userId = pathParts[0];
                                            
                                            if (!filesByUser[userId]) {
                                                filesByUser[userId] = [];
                                            }
                                            filesByUser[userId].push(file);
                                        });

                                        return (
                                            <div className="files-grouped">
                                                {Object.entries(filesByUser).map(([userId, files]) => (
                                                    <div key={userId} className="file-group">
                                                        <div className="file-group-header">
                                                            <span className="user-id">
                                                                {loadingEmails ? (
                                                                    `${userId} (loading...)`
                                                                ) : (
                                                                    userEmailMap[userId] || userId
                                                                )}
                                                            </span>
                                                            <span className="file-count">{files.length} file{files.length !== 1 ? 's' : ''}</span>
                                                        </div>
                                                        <div className="files-list">
                                                            {files.map((file, index) => {
                                                                // Extract just the filename
                                                                const displayName = file.key.split('/').pop();
                                                                
                                                                return (
                                                                    <div key={index} className="file-item">
                                                                        <div className="file-info">
                                                                            <span className="file-name">{displayName}</span>
                                                                            <span className="file-date">{new Date(file.lastModified).toLocaleString()}</span>
                                                                            <span className="file-size">{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                                                                        </div>
                                                                        <div className="file-actions">
                                                                            <button 
                                                                                onClick={() => handleDownload(file.key)}
                                                                                className="file-action-btn download"
                                                                                title="Download file"
                                                                            >
                                                                                ⬇ Download
                                                                            </button>
                                                                            <button 
                                                                                onClick={() => handleDelete(file.key, displayName)}
                                                                                className="file-action-btn delete"
                                                                                title="Delete file"
                                                                            >
                                                                                🗑 Delete
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()
                                ) : (
                                    // Regular user view: Simple list
                                    <div className="files-list">
                                        {uploadedFiles.map((file, index) => {
                                            // Extract filename, removing user ID folder
                                            const displayName = file.key.replace('forensic-images/', '').replace('public/forensic-images/', '').replace(/^[^/]+\//, '');
                                            
                                            return (
                                                <div key={index} className="file-item">
                                                    <div className="file-info">
                                                        <span className="file-name">{displayName}</span>
                                                        <span className="file-date">{new Date(file.lastModified).toLocaleString()}</span>
                                                        <span className="file-size">{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                                                    </div>
                                                    <div className="file-actions">
                                                        <button 
                                                            onClick={() => handleDownload(file.key)}
                                                            className="file-action-btn download"
                                                            title="Download file"
                                                        >
                                                            ⬇ Download
                                                        </button>
                                                        <button 
                                                            onClick={() => handleDelete(file.key, displayName)}
                                                            className="file-action-btn delete"
                                                            title="Delete file"
                                                        >
                                                            🗑 Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </main>

            <footer className="App-footer">
                <p>Files are uploaded with S3 Transfer Acceleration for optimal performance</p>
            </footer>

            {/* Modal for messages */}
            {(successMessage || error) && (
                <div className="modal-overlay" onClick={() => { setSuccessMessage(''); setError(''); }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className={`modal-message ${error ? 'error' : 'success'}`}>
                            {error ? (
                                <>
                                    <div className="modal-icon">⚠️</div>
                                    <div className="modal-text">
                                        <h3>Error</h3>
                                        <p>{error}</p>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="modal-icon">✓</div>
                                    <div className="modal-text">
                                        <h3>Success</h3>
                                        <p>{successMessage}</p>
                                    </div>
                                </>
                            )}
                        </div>
                        <button 
                            className="modal-close-btn" 
                            onClick={() => { setSuccessMessage(''); setError(''); }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function AdminPanel({ userPoolId }) {
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserRole, setNewUserRole] = useState('Users');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [activeTab, setActiveTab] = useState('list');

    useEffect(() => {
        if (activeTab === 'list') {
            loadUsers();
        }
    }, [activeTab]);

    const loadUsers = async () => {
        setLoadingUsers(true);
        setError('');
        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();
            
            if (!token) {
                throw new Error('No authentication token available');
            }
            
            console.log('Fetching users from:', `${process.env.REACT_APP_API_ENDPOINT}/list-users`);
            
            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/list-users`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            console.log('Response status:', response.status);
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response:', errorText);
                let errorMessage = 'Failed to load users';
                try {
                    const errorData = JSON.parse(errorText);
                    errorMessage = errorData.error || errorMessage;
                } catch {
                    errorMessage = `HTTP ${response.status}: ${errorText || response.statusText}`;
                }
                throw new Error(errorMessage);
            }
            
            const data = await response.json();
            setUsers(data.users || []);
        } catch (err) {
            console.error('Load users error:', err);
            console.error('Error details:', {
                message: err.message,
                stack: err.stack,
                apiEndpoint: process.env.REACT_APP_API_ENDPOINT
            });
            setError(err.message || 'Failed to load users');
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');
        setLoading(true);
        
        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();
            
            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/create-user`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    email: newUserEmail,
                    role: newUserRole
                })
            });
            
            const data = await response.json();
            
            if (response.ok) {
                setMessage(`User ${newUserEmail} created successfully! Welcome email sent with temporary password.`);
                setNewUserEmail('');
                if (activeTab === 'list') {
                    loadUsers();
                }
            } else {
                setError(data.error || 'Failed to create user');
            }
        } catch (err) {
            console.error('Create user error:', err);
            setError(err.message || 'Failed to create user');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async (username) => {
        if (!window.confirm(`Are you sure you want to delete user ${username}?`)) {
            return;
        }

        setError('');
        setMessage('');
        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();
            
            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/delete-user`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ username })
            });
            
            const data = await response.json();
            
            if (response.ok) {
                setMessage(`User ${username} deleted successfully`);
                loadUsers();
            } else {
                setError(data.error || 'Failed to delete user');
            }
        } catch (err) {
            console.error('Delete user error:', err);
            setError(err.message || 'Failed to delete user');
        }
    };

    const handleResetPassword = async (username) => {
        if (!window.confirm(`Reset password for ${username}? They will receive a temporary password.`)) {
            return;
        }

        setError('');
        setMessage('');
        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();
            
            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ username })
            });
            
            const data = await response.json();
            
            if (response.ok) {
                setMessage(`Password reset email sent to ${username}.`);
            } else {
                setError(data.error || 'Failed to reset password');
            }
        } catch (err) {
            console.error('Reset password error:', err);
            setError(err.message || 'Failed to reset password');
        }
    };

    const handleToggleUserStatus = async (username, currentStatus) => {
        const action = currentStatus === 'ENABLED' ? 'disable' : 'enable';
        if (!window.confirm(`Are you sure you want to ${action} user ${username}?`)) {
            return;
        }

        setError('');
        setMessage('');
        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();
            
            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/toggle-user`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ username, enable: currentStatus !== 'ENABLED' })
            });
            
            const data = await response.json();
            
            if (response.ok) {
                setMessage(`User ${username} ${action}d successfully`);
                loadUsers();
            } else {
                setError(data.error || `Failed to ${action} user`);
            }
        } catch (err) {
            console.error('Toggle user error:', err);
            setError(err.message || `Failed to ${action} user`);
        }
    };

    const handleChangeUserRole = async (username, currentGroups) => {
        const isAdmin = currentGroups.includes('Admins');
        const newRole = isAdmin ? 'Users' : 'Admins';
        
        if (!window.confirm(`Change ${username} role to ${newRole}?`)) {
            return;
        }

        setError('');
        setMessage('');
        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();
            
            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/change-role`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ username, role: newRole })
            });
            
            const data = await response.json();
            
            if (response.ok) {
                setMessage(`User ${username} role changed to ${newRole}`);
                loadUsers();
            } else {
                setError(data.error || 'Failed to change role');
            }
        } catch (err) {
            console.error('Change role error:', err);
            setError(err.message || 'Failed to change role');
        }
    };

    return (
        <div className="admin-panel">
            <h2>Admin Panel</h2>
            
            {message && <div className="success-message">{message}</div>}
            {error && <div className="error-message">{error}</div>}

            <div className="admin-tabs">
                <button 
                    className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
                    onClick={() => setActiveTab('list')}
                >
                    Manage Users
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'create' ? 'active' : ''}`}
                    onClick={() => setActiveTab('create')}
                >
                    Create User
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'credentials' ? 'active' : ''}`}
                    onClick={() => setActiveTab('credentials')}
                >
                    Manage Credentials
                </button>
            </div>

            {activeTab === 'create' && (
                <div className="admin-section">
                    <h3>Create New User</h3>
                    <form onSubmit={handleCreateUser} className="admin-form">
                        <div className="form-group">
                            <label>Email</label>
                            <input
                                type="email"
                                value={newUserEmail}
                                onChange={(e) => setNewUserEmail(e.target.value)}
                                required
                                disabled={loading}
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Role</label>
                            <select value={newUserRole} onChange={(e) => setNewUserRole(e.target.value)} disabled={loading}>
                                <option value="Users">User</option>
                                <option value="Admins">Admin</option>
                            </select>
                        </div>
                        
                        <button type="submit" className="admin-btn" disabled={loading}>
                            {loading ? 'Creating...' : 'Create User'}
                        </button>
                        <p className="admin-note">User will receive a welcome email with temporary password.</p>
                    </form>
                </div>
            )}

            {activeTab === 'list' && (
                <div className="admin-section">
                    <div className="users-header">
                        <h3>User Management</h3>
                        <button onClick={loadUsers} className="refresh-btn" disabled={loadingUsers}>
                            {loadingUsers ? 'Loading...' : 'Refresh'}
                        </button>
                    </div>

                    {loadingUsers ? (
                        <p>Loading users...</p>
                    ) : users.length === 0 ? (
                        <p>No users found</p>
                    ) : (
                        <div className="users-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Email</th>
                                        <th>Status</th>
                                        <th>Role</th>
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((user) => (
                                        <tr key={user.username}>
                                            <td>{user.email}</td>
                                            <td>
                                                <span className={`status-badge ${user.enabled ? 'enabled' : 'disabled'}`}>
                                                    {user.enabled ? 'Enabled' : 'Disabled'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`role-badge ${user.groups.includes('Admins') ? 'admin' : 'user'}`}>
                                                    {user.groups.includes('Admins') ? 'Admin' : 'User'}
                                                </span>
                                            </td>
                                            <td>{new Date(user.created).toLocaleDateString()}</td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button 
                                                        onClick={() => handleResetPassword(user.username)}
                                                        className="action-btn reset"
                                                        title="Reset Password"
                                                    >
                                                        Reset Password
                                                    </button>
                                                    <button 
                                                        onClick={() => handleChangeUserRole(user.username, user.groups)}
                                                        className="action-btn role"
                                                        title="Change Role"
                                                    >
                                                        {user.groups.includes('Admins') ? 'Set as User' : 'Set as Admin'}
                                                    </button>
                                                    <button 
                                                        onClick={() => handleToggleUserStatus(user.username, user.status)}
                                                        className={`action-btn ${user.enabled ? 'disable' : 'enable'}`}
                                                        title={user.enabled ? 'Disable User' : 'Enable User'}
                                                    >
                                                        {user.enabled ? 'Disable' : 'Enable'}
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDeleteUser(user.username)}
                                                        className="action-btn delete"
                                                        title="Delete User"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'credentials' && (
                <ManageCredentials />
            )}
        </div>
    );
}

export default App;
